import {
  DataSourceDatabaseId,
  DataSourceSchemaId,
  DataSourceTableId,
  SemanticDatasetName,
  SemanticProjectFileId,
  SemanticProjectId,
  uuid,
} from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Draft } from "immer";
import { DistributedOmit } from "type-fest";

import { SemanticProjectProblem } from "../../components/semantic-project-workbench/problems/types";
import { SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID } from "../../components/semantic-project-workbench/sidebar/storage";
import {
  WorkbenchTab,
  WorkbenchTabGroup,
  WorkbenchTabGroupId,
  WorkbenchTabId,
  WorkbenchViewport,
  closeTab,
  focusTab,
  newTab,
  updateTab,
  updateTabGroupsToMatchViewport,
  updateViewportToMatchTabGroups,
} from "../../components/workbench/state";
import {
  getStorageValue,
  setStorageValue,
} from "../../hooks/useBrowserStorage";
import { RootState } from "../store";

// *** State *****************************************************************
export type SemanticProjectWorkbenchState = {
  projectId: SemanticProjectId | null;
  lastProjectId: SemanticProjectId | null;
  tabGroups: WorkbenchTabGroup<SemanticProjectWorkbenchEditorTab>[];
  viewport: WorkbenchViewport;
  mainSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  bottomSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  rightSidebar: { panelId: SemanticProjectWorkbenchSidebarPanelId | null };
  problems: {
    /** problems scanned for on a regular schedule by client code, containing
     * simple problems we can catch without needing to run the build. */
    clientSide: SemanticProjectProblem[];
  };
};

export type SemanticProjectWorkbenchEditorTab =
  | SemanticProjectWorkbenchEditorTab.FileEditor
  | SemanticProjectWorkbenchEditorTab.FileDiffEditor
  | SemanticProjectWorkbenchEditorTab.NewDatasetFile;
export namespace SemanticProjectWorkbenchEditorTab {
  type _Base<TK extends string, X> = WorkbenchTab & { type: TK } & X;
  export type FileEditor = _Base<
    "fileEditor",
    { fileId: SemanticProjectFileId }
  >;
  export type FileDiffEditor = _Base<
    "fileDiffEditor",
    { fileId: SemanticProjectFileId; view: "inline" | "side-by-side" }
  >;
  export type NewDatasetFile = _Base<
    "newDatasetFile",
    { formState: SemanticProjectWorkbenchNewDatasetFileData }
  >;
}

export type SemanticProjectWorkbenchNewDatasetFileData = {
  name: SemanticDatasetName;
  description: string;
  base:
    | {
        type: "table";
        structuredTable: {
          table: { id: DataSourceTableId; name: string };
          schema: { id: DataSourceSchemaId; name: string };
          database: { id: DataSourceDatabaseId; name: string | null } | null;
        } | null;
      }
    | { type: "sql"; sql: string }
    | null;
  import: {
    columnsAsDimensions: boolean;
    columnDescriptions: boolean;
  };
};
export namespace SemanticProjectWorkbenchNewDatasetFileData {
  export const EMPTY: SemanticProjectWorkbenchNewDatasetFileData = {
    name: SemanticDatasetName.check(""),
    description: "",
    base: null,
    import: { columnsAsDimensions: true, columnDescriptions: true },
  };
}

export type SemanticProjectWorkbenchSidebarPanelId =
  | "files"
  | "dataBrowser"
  | "help"
  | "problems"
  | "chat"
  | "history"
  // deprecated, but may be present in storage
  | "datasets";

const initialState: SemanticProjectWorkbenchState = {
  projectId: null,
  lastProjectId: null,
  mainSidebar: { panelId: "files" },
  bottomSidebar: { panelId: null },
  rightSidebar: { panelId: "chat" },
  tabGroups: [],
  viewport: { type: "empty" },
  problems: { clientSide: [] },
};

// *** Actions ****************************************************************
const semanticProjectWorkbenchSlice = createSlice({
  name: "semanticProjectWorkbench",
  initialState,
  reducers: {
    /**
     * Initialize the workbench state for a given project. Preserves existing
     * state if the project is the same.
     */
    init(state, action: PayloadAction<{ projectId: SemanticProjectId }>) {
      if (state.lastProjectId !== action.payload.projectId) {
        // must return so redux has new reference
        const rightSidebarPanelId = getStorageValue(
          SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID({
            semanticProjectId: action.payload.projectId,
          }),
          "Local",
        );
        return {
          ...initialState,
          projectId: action.payload.projectId,
          lastProjectId: state.lastProjectId,
          rightSidebar: {
            ...initialState.rightSidebar,
            panelId: rightSidebarPanelId,
          },
        };
      }

      state.projectId = action.payload.projectId;
    },
    unmount(state) {
      state.lastProjectId = state.projectId;
      state.projectId = null;
    },

    /** Change a sidebar's panel. */
    setSidebarPanel(
      state,
      action: PayloadAction<{
        sidebar: "main" | "bottom" | "right";
        panelId: SemanticProjectWorkbenchSidebarPanelId | null;
        /** if true, will instead toggle the panel if the panel with that id is already open. */
        toggleIfOpen?: boolean;
      }>,
    ) {
      const sidebarKey = `${action.payload.sidebar}Sidebar` as const;
      let value: SemanticProjectWorkbenchSidebarPanelId | null;
      if (
        action.payload.toggleIfOpen &&
        state[sidebarKey].panelId === action.payload.panelId
      ) {
        value = null;
      } else {
        value = action.payload.panelId;
      }
      state[sidebarKey].panelId = value;

      if (state.projectId && sidebarKey === "rightSidebar") {
        setStorageValue(
          SEMANTIC_PROJECT_WORKBENCH_RIGHT_SIDEBAR_PANEL_ID({
            semanticProjectId: state.projectId,
          }),
          value,
          "Local",
        );
      }
    },

    /** Bindings for problem tracking */
    setClientSideProblems(
      state,
      action: PayloadAction<{
        reporter: SemanticProjectProblem["reporter"];
        problems: SemanticProjectProblem[];
      }>,
    ) {
      if (
        !action.payload.problems.every(
          (p) => p.reporter === action.payload.reporter,
        )
      ) {
        throw new Error("Problems must all have the same reporter");
      }
      // remove all existing problems from this reporter
      state.problems.clientSide = state.problems.clientSide.filter(
        (p) => p.reporter !== action.payload.reporter,
      );
      // insert all the problems
      state.problems.clientSide.push(...action.payload.problems);
    },

    // *** Bindings for tab APIs ***
    newEditorTab(
      state,
      action: PayloadAction<{
        tab: DistributedOmit<SemanticProjectWorkbenchEditorTab, "id">;
        tabGroupId: WorkbenchTabGroupId | null;
      }>,
    ) {
      state.tabGroups = newTab(
        state.tabGroups,
        { id: WorkbenchTabId.check(uuid()), ...action.payload.tab },
        action.payload.tabGroupId,
      );
      state.viewport = updateViewportToMatchTabGroups(
        state.tabGroups,
        state.viewport,
      );
    },
    focusEditorTab(
      state,
      action: PayloadAction<{
        tabId: WorkbenchTabId;
        promotePreview?: boolean;
      }>,
    ) {
      state.tabGroups = focusTab(
        state.tabGroups,
        action.payload.tabId,
        action.payload.promotePreview,
      );
      state.viewport = updateViewportToMatchTabGroups(
        state.tabGroups,
        state.viewport,
      );
    },
    closeEditorTab(state, action: PayloadAction<{ tabId: WorkbenchTabId }>) {
      state.tabGroups = closeTab(state.tabGroups, action.payload.tabId);
      state.viewport = updateViewportToMatchTabGroups(
        state.tabGroups,
        state.viewport,
      );
    },
    updateEditorTab(
      state,
      action: PayloadAction<{
        tabId: WorkbenchTabId;
        producerFn: (
          tab: Draft<SemanticProjectWorkbenchEditorTab>,
        ) => void | Draft<SemanticProjectWorkbenchEditorTab>;
      }>,
    ) {
      state.tabGroups = updateTab(
        state.tabGroups,
        action.payload.tabId,
        action.payload.producerFn,
      );
    },
    updateEditorViewport(state, action: PayloadAction<WorkbenchViewport>) {
      state.viewport = action.payload;
      state.tabGroups = updateTabGroupsToMatchViewport(
        action.payload,
        state.tabGroups,
      );
    },
  },
});

export const semanticProjectWorkbenchReducer =
  semanticProjectWorkbenchSlice.reducer;

export const semanticProjectWorkbenchActions =
  semanticProjectWorkbenchSlice.actions;

// *** Selectors **************************************************************
export function selectSemanticProjectWorkbench(
  state: RootState,
): SemanticProjectWorkbenchState {
  return state.semanticProjectWorkbench;
}

export function selectSemanticProjectWorkbenchEditorPrimaryTabGroupId(
  state: RootState,
): WorkbenchTabGroupId | null {
  return state.semanticProjectWorkbench.tabGroups[0]?.id ?? null;
}
